import {
  fetchActiveIntegrations,
  fetchActiveIntegrationsFailure,
  fetchActiveIntegrationsRequest,
  fetchActiveIntegrationsSuccess,
  fetchAllCampaigns,
  fetchAllCampaignsFailure,
  fetchAllCampaignsRequest,
  fetchAllCampaignsSuccess,
  fetchCampaignsForFilterPills,
  fetchCampaignsForFilterPillsFailure,
  fetchCampaignsForFilterPillsRequest,
  fetchCampaignsForFilterPillsSuccess,
  fetchFilterCampaigns,
  fetchSingleCampaign,
  fetchSingleCampaignFailure,
  fetchSingleCampaignRequest,
  fetchSingleCampaignSuccess,
} from 'actions';
import { receiveRuleOutcomes } from 'actions/campaignRuleOutcomes';
import { cleanOutcomes } from 'actions/outcome';
import { apiClient as LitLingoClient } from 'client';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getFiltersSearchString } from 'selectors/envelopes';
import { getNavParams, getNavParamsByResource } from 'selectors/nav';
import type { API, MCampaign, RouteParams, SagaReturn } from 'types';

function* fetchSingleCampaignSaga(action: ReturnType<typeof fetchSingleCampaign>): SagaReturn {
  const { payload } = action;
  yield put(cleanOutcomes());
  yield put(fetchSingleCampaignRequest());
  const response = (yield call(
    [LitLingoClient.resources.campaigns, 'retrieve'],
    payload.campaignId,
    {
      params: {
        relationships: [
          'rule_outcomes.rule',
          'rule_outcomes.outcome',
          'rule_outcomes.created_by',
          'rule_outcomes.rule.updated_by',
          'rule_outcomes.rule.created_by',
          'rule.annotator',
        ],
        include_pii: 'true',
      },
    }
  )) as API.Response<API.Campaigns.Retrieve>;
  if (response.error != null) {
    yield put(fetchSingleCampaignFailure(response.error));
  } else {
    // Strip rule Outcomes out of the campaign - just leave an array of ID's
    const { rule_outcomes: ruleOutcomes, ...singleCampaign } = response.data;
    const campaign: MCampaign = { ...singleCampaign };
    if (ruleOutcomes != null) {
      campaign.rules = ruleOutcomes.map((ruleOutcome) => ruleOutcome.rule_uuid);
      yield put(receiveRuleOutcomes(ruleOutcomes));
    }

    yield put(fetchSingleCampaignSuccess(campaign));
  }
}

function* fetchCampaignsList(paramsArg: Partial<RouteParams>): SagaReturn {
  yield put(fetchAllCampaignsRequest());
  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.campaign)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;
  const params = { include_count: true, ...paramsArg, ...resourceParams };

  const response = (yield call([LitLingoClient.resources.campaigns, 'list'], {
    params,
  })) as API.Response<API.Campaigns.List>;
  if (response.error != null) {
    yield put(fetchAllCampaignsFailure(response.error));
  } else {
    yield put(fetchAllCampaignsSuccess(response.data));
  }
}

function* fetchCampaignsForFilterPillsSaga(
  action: ReturnType<typeof fetchCampaignsForFilterPills>
): SagaReturn {
  const { campaignIds } = action.payload;

  const params = { uuids: campaignIds, include_pii: true };
  yield put(fetchCampaignsForFilterPillsRequest());

  const response = (yield call([LitLingoClient.resources.campaigns, 'list'], {
    params,
  })) as API.Response<API.Campaigns.List>;

  if (response.error != null) {
    yield put(fetchCampaignsForFilterPillsFailure(response.error));
  } else {
    yield put(fetchCampaignsForFilterPillsSuccess(response.data));
  }
}

function* fetchAllCampaignsSaga({ payload }: ReturnType<typeof fetchAllCampaigns>): SagaReturn {
  const navParams = (yield select(getNavParams)) as RouteParams;
  const params = {
    ...navParams,
    include_pii: 'true',
    relationships: [
      'rule_outcomes.rule',
      'rule_outcomes.rule.campaign_rule_outcomes',
      'rule_outcomes.rule.campaign_rule_outcomes.outcome',
      'updated_by',
    ],
    ...payload,
  };

  yield call(fetchCampaignsList, params);
}

function* fetchFilterCampaignsSaga({
  payload,
}: ReturnType<typeof fetchFilterCampaigns>): SagaReturn {
  const navParams = (yield select(getNavParams)) as RouteParams;

  const filtersSearch = (yield select(getFiltersSearchString)) as ReturnType<
    typeof getFiltersSearchString
  >;

  const params = {
    ...navParams,
    ...payload,
    include_pii: 'true',
    selectable_fields: ['uuid', 'name'],
    active: 'true',
    filters_search: filtersSearch,
  };
  yield call(fetchCampaignsList, params);
}

function* fetchActiveIntegrationsSaga(
  action: ReturnType<typeof fetchActiveIntegrations>
): SagaReturn {
  const { payload } = action;
  yield put(fetchActiveIntegrationsRequest());

  const response = (yield call(
    [LitLingoClient.resources.campaigns.extras, 'getActiveIntegrations'],
    { params: { broad_search: payload.searchValue } }
  )) as API.Response<API.Campaigns.getActiveIntegrations>;
  if (response.error != null) {
    yield put(fetchActiveIntegrationsFailure(response.error));
  } else {
    yield put(fetchActiveIntegrationsSuccess(response.data));
  }
}

function* campaignsSaga(): SagaReturn {
  yield takeLatest(fetchAllCampaigns.toString(), fetchAllCampaignsSaga);
  yield takeEvery(fetchCampaignsForFilterPills.toString(), fetchCampaignsForFilterPillsSaga);

  yield takeLatest(fetchSingleCampaign.toString(), fetchSingleCampaignSaga);

  yield takeLatest(fetchActiveIntegrations.toString(), fetchActiveIntegrationsSaga);
  yield takeLatest(fetchFilterCampaigns.toString(), fetchFilterCampaignsSaga);
}

export default campaignsSaga;
